import { useState } from 'react';
import { CloseCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Descriptions, ModalProps, Result, Space, Spin } from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import Dropdown, { DropdownButtonProps } from 'antd/es/dropdown';

import { NodeSupplierContractWithDetails } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';

import { useDeleteNodeSupplierContract, useGetNodeSupplierContract } from '../data';
import { CreateForm, EditForm } from '../forms';

export const useNodeSupplierContractActions = () => {
  const [detailsId, setDetailsId] = useState<number | undefined>();
  const [deleteId, setDeleteId] = useState<number | undefined>();

  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editId, setEditId] = useState<number | undefined>();

  const deleter = useDeleteNodeSupplierContract();

  const detailsNode = useGetNodeSupplierContract(detailsId ?? 0, { enabled: !!detailsId });
  const deleteNode = useGetNodeSupplierContract(deleteId ?? 0, { enabled: !!deleteId });

  const toggleCreateModal = () => {
    setDetailsId(undefined);
    setDeleteId(undefined);
    setEditId(undefined);
    setCreateModalOpen((prevValue) => !prevValue);
  };

  const toggleEditModal = (id?: number) => {
    setDetailsId(undefined);
    setDeleteId(undefined);
    setCreateModalOpen(false);
    setEditId(id);
  };

  const toggleDeleteModal = (id?: number) => {
    setDetailsId(undefined);
    setEditId(undefined);
    setCreateModalOpen(false);
    setDeleteId(id);
  };

  const toggleDetailsModal = (id?: number) => {
    setDeleteId(undefined);
    setEditId(undefined);
    setCreateModalOpen(false);
    setDetailsId(id);
  };

  const getCreateModalProps = (props?: ModalProps): ModalProps => ({
    ...props,
    open: createModalOpen,
    children: <CreateForm onCancel={toggleCreateModal} />,
    onCancel: toggleCreateModal,
    maskClosable: false,
    footer: null,
    width: 800,
    title: 'Register Node Supplier Contract',
    destroyOnClose: true,
  });

  const getEditModalProps = (props?: ModalProps): ModalProps => ({
    ...props,
    open: !!editId,
    onCancel: () => {
      toggleEditModal();
    },
    children: (
      <EditForm
        onCancel={() => {
          toggleEditModal();
        }}
        id={editId}
      />
    ),
    maskClosable: false,
    footer: null,
    width: 800,
    title: 'Edit Node Supplier Contract',
    destroyOnClose: true,
  });

  const _getDescriptionItems = (data?: NodeSupplierContractWithDetails): DescriptionsItemType[] => [
    { key: 'Supplier', children: data?.supplier?.name || data?.supplierName, label: 'Supplier' },
    { key: 'Node', children: data?.node.name, label: 'Node' },
    { key: 'Contract', children: data?.contractName, label: 'Contract' },
    { key: 'Contract Number', children: data?.contractNumber, label: 'Contract Number' },
    { key: 'Intel Signed Date', children: formatIsoDate(data?.intelSignedDate), label: 'Intel Signed Date' },
  ];

  const getActionButton = (record: { id: number }, props?: DropdownButtonProps) => (
    <Dropdown.Button
      size="small"
      {...props}
      menu={{
        items: [
          { label: 'Edit', key: 'edit', icon: <EditOutlined /> },
          { label: 'Delete', key: 'delete', icon: <DeleteOutlined /> },
        ],
        onClick: (e) => {
          switch (e.key) {
            case 'edit':
              toggleEditModal(record.id);
              break;
            case 'delete':
              toggleDeleteModal(record.id);
              break;
            default:
              console.log(`Cannot handle key: ${e.key}`);
              break;
          }
        },
      }}
      onClick={() => toggleDetailsModal(record.id)}
    >
      <Space>
        <EyeOutlined />
        Details
      </Space>
    </Dropdown.Button>
  );

  const getDetailsModalProps = (props?: ModalProps): ModalProps => {
    const footer = [
      <Button icon={<DeleteOutlined />} key="delete" onClick={() => detailsId && toggleDeleteModal(detailsId)}>
        Delete
      </Button>,
      <Button icon={<EditOutlined />} key="edit" onClick={() => detailsId && toggleEditModal(detailsId)}>
        Edit
      </Button>,
      <Button key="close" onClick={() => toggleDetailsModal()} icon={<CloseCircleOutlined />} type="primary">
        Close
      </Button>,
    ];

    return {
      centered: true,
      title: `${detailsNode.data?.supplierName} Details`,
      width: 1100,
      ...props,

      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Descriptions
            bordered
            size="small"
            layout="horizontal"
            labelStyle={{ width: 150 }}
            column={1}
            items={_getDescriptionItems(detailsNode.data)}
          />
        </Space>
      ),
      footer,
      open: !!detailsId,
      onCancel: () => toggleDetailsModal(),
    };
  };

  const getDeleteModalProps = (props?: ModalProps): ModalProps => {
    const onCancel = () => {
      toggleDeleteModal();
      deleter.reset();
    };

    const onOk = () => {
      if (deleter.isIdle || deleter.isError) {
        deleteId && deleter.mutate(deleteId);
      } else {
        onCancel();
      }
    };

    return {
      centered: true,
      title: 'Are you sure you want to delete this node-supplier contract?',
      ...props,
      children: deleter.isPending ? (
        <Spin style={{ width: '100%' }} />
      ) : deleter.isSuccess ? (
        <Result
          status="success"
          title="Successfully deleted node-supplier contract"
          subTitle={`Node-Supplier Contract ${deleteNode.data?.contractName} has been deleted`}
        />
      ) : deleter.isError ? (
        <Result
          status="error"
          title="Error occured when deleting node-supplier contract"
          subTitle={deleter.error.response?.data?.message}
        />
      ) : (
        <Descriptions
          bordered
          size="small"
          layout="horizontal"
          column={1}
          labelStyle={{ width: 150 }}
          items={_getDescriptionItems(deleteNode.data)}
        />
      ),
      cancelButtonProps: {
        style: deleter.isSuccess ? { display: 'none' } : undefined,
        disabled: deleter.isPending,
      },
      okButtonProps: {
        icon: deleter.isSuccess ? <CloseCircleOutlined /> : <DeleteOutlined />,
        disabled: deleter.isPending,
      },
      okText: deleter.isIdle || deleter.isPending ? 'Delete' : deleter.isError ? 'Retry' : 'Close',
      okType: deleter.isSuccess ? undefined : 'danger',
      open: !!deleteId,
      width: 800,
      onCancel,
      onOk,
    };
  };

  return {
    getActionButton,
    getDeleteModalProps,
    getDetailsModalProps,
    toggleCreateModal,
    toggleEditModal,
    getCreateModalProps,
    getEditModalProps,
  };
};
